import { uniqueIdentifierForNode } from '@ctw/shared/content/document-viewer/_internal/renderers/helpers';
import { Text, View } from '@react-pdf/renderer';
import { isEmpty, trim } from 'lodash-es';
import { Fragment } from 'react';
import type { CdaSection } from '../../parsers/types';
import {
  isCdataSection,
  isHeadingElement,
  isHtmlElement,
  isInlineTextElement,
  isListElement,
  isTableElement,
  isTextNode,
} from '../helpers';
import { styles } from './pdf-cda-renderer';
import { PdfHeadingRenderer } from './pdf-heading-renderer';
import { PdfListRenderer } from './pdf-list-renderer';
import { PdfTableRenderer } from './pdf-table-renderer';

interface PdfNodeRendererProps {
  section: CdaSection;
  node: Node;
}

export const PdfNodeRenderer = ({ section, node }: PdfNodeRendererProps) => {
  if (isTextNode(node) || isCdataSection(node)) {
    const text = trim(node.textContent ?? '');

    if (!isEmpty(text)) {
      return !isEmpty(text) ? (
        <Text
          key={uniqueIdentifierForNode({ section, node, context: 'node-generictext' })}
          style={styles.text}
        >
          {text}
        </Text>
      ) : null;
    }

    // Don't render empty text nodes
    return <></>;
  }

  if (isHtmlElement(node)) {
    if (node.nodeName === 'content') {
      return (
        <View style={styles.container}>
          {Array.from(node.childNodes).map((child) => (
            <PdfNodeRenderer
              key={uniqueIdentifierForNode({ section, node: child, context: 'node-heading' })}
              section={section}
              node={child}
            />
          ))}
        </View>
      );
    }

    if (isHeadingElement(node)) {
      return (
        <PdfHeadingRenderer
          key={uniqueIdentifierForNode({ section, node, context: 'node-heading' })}
          section={section}
          heading={node}
        />
      );
    }

    if (isListElement(node)) {
      return (
        <PdfListRenderer
          key={uniqueIdentifierForNode({ section, node, context: 'node-list' })}
          section={section}
          list={node}
        />
      );
    }

    if (isTableElement(node)) {
      return (
        <PdfTableRenderer
          key={uniqueIdentifierForNode({ section, node, context: 'node-table' })}
          section={section}
          table={node}
        />
      );
    }

    if (isInlineTextElement(node)) {
      return (
        <Fragment key={uniqueIdentifierForNode({ section, node, context: 'node-paragraph' })}>
          {Array.from(node.childNodes).map((child) => (
            <PdfNodeRenderer
              key={uniqueIdentifierForNode({
                section,
                node: child,
                context: 'node-inlinetext-child',
              })}
              node={child}
              section={section}
            />
          ))}
        </Fragment>
      );
    }
  }

  // Any node that is not Text, CDATA, or an HTML Element should not be rendered.
  return <></>;
};
